/**
 * Android WebViews disable DOM storage by default (window.sessionStorage is null),
 * and browsers that block site data can throw a SecurityError on access.
 */
const getWebStorage = (): Storage | null => {
  // Client-only: on the server, fail loudly rather than share one memory store
  // across every prerendered page.
  if (!globalThis.window) {
    throw new Error("session storage helpers are client-only");
  }
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
};

// Values that couldn't be written to Web Storage live in memory for the current
// page session, so a later read (e.g. after client-side navigation) still sees
// an earlier write. Memory only ever holds a key's latest failed write, so it
// is read first.
const memoryStorage = new Map<string, string>();

export const getSessionItem = (key: string): string | null => {
  const webStorage = getWebStorage();
  const memoryValue = memoryStorage.get(key);
  if (memoryValue !== undefined) {
    return memoryValue;
  }
  try {
    return webStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

export const setSessionItem = (key: string, value: string): void => {
  const webStorage = getWebStorage();
  try {
    if (webStorage) {
      webStorage.setItem(key, value);
      memoryStorage.delete(key);
      return;
    }
  } catch {
    // Storage can exist but reject writes (e.g. QuotaExceededError when full).
  }
  memoryStorage.set(key, value);
};
